'use client'

import { RefObject, useEffect, useRef, useState } from 'react'

import { ArrowUp } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'

import { cn } from '@/lib/utils/cn'

interface BackTopProps {
  /** 滚动高度达到此参数值才出现 BackTop */
  visibilityHeight?: number
  /** 自定义类名 */
  className?: string
  /** 自定义图标 */
  icon?: React.ReactNode
  /** 监听滚动的目标元素 ref，默认为 window */
  target?: RefObject<HTMLElement | null>
  /** 点击回调 */
  onClick?: () => void
}

const DEFAULT_VISIBILITY_HEIGHT = 1000

/**
 * 回到顶部组件
 */
export function BackTop({
  visibilityHeight = DEFAULT_VISIBILITY_HEIGHT,
  className,
  icon,
  target,
  onClick
}: BackTopProps) {
  const [visible, setVisible] = useState(false)
  const rafRef = useRef<number>(null)

  useEffect(() => {
    const targetElement = target?.current
    const scrollTarget = targetElement || window

    const handleScroll = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      rafRef.current = requestAnimationFrame(() => {
        const scrollTop = targetElement
          ? targetElement.scrollTop
          : window.scrollY || document.documentElement.scrollTop
        setVisible(scrollTop >= visibilityHeight)
      })
    }

    scrollTarget.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => {
      scrollTarget.removeEventListener('scroll', handleScroll)
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [target, visibilityHeight])

  const scrollToTop = () => {
    onClick?.()
    const targetElement = target?.current
    if (targetElement) {
      targetElement.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          animate={{ opacity: 1, scale: 1, y: 0 }}
          aria-label="Back to top"
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          type="button"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className={cn(
            'fixed right-6 bottom-6 z-50 flex size-11 cursor-pointer items-center justify-center rounded-full',
            'bg-white/10 backdrop-blur-md',
            'border border-white/10',
            'shadow-lg shadow-black/20',
            'hover:bg-white/20 hover:border-white/20',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
            className
          )}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25
          }}
          onClick={scrollToTop}
        >
          <motion.div
            animate={{ y: [0, -3, 0] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          >
            {icon ?? <ArrowUp className="size-5 text-white/80" />}
          </motion.div>
        </motion.button>
      )}
    </AnimatePresence>
  )
}
